import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box,
    TextField, MenuItem, Button
} from '@mui/material';
import { formatForInput } from '../../utils/dateUtils';
import { validateEmail } from '../../utils/validation';

const AU_STATES = [
    { value: 'NSW', label: 'NSW' },
    { value: 'VIC', label: 'VIC' },
    { value: 'QLD', label: 'QLD' },
    { value: 'WA', label: 'WA' },
    { value: 'SA', label: 'SA' },
    { value: 'TAS', label: 'TAS' },
    { value: 'ACT', label: 'ACT' },
    { value: 'NT', label: 'NT' },
];

const STATUS_OPTIONS = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'VALID', label: 'Valid' },
    { value: 'INVALID', label: 'Invalid' },
    { value: 'EXPIRED', label: 'Expired' },
    { value: 'BANNED', label: 'Banned' },
];

const INITIAL_FORM = {
    id: null,
    au_state: '',
    first_given_name: '',
    other_given_names: '',
    surname: '',
    birthdate: '',
    wwcc_number: '',
    email: '',
    expiry_date: '',
    is_active: true,
    status: 'PENDING',
    notes: ''
};


export default function WWCCDialog({ open, onClose, onSave, record, isEditing }) {
    const [form, setForm] = useState(INITIAL_FORM);
    const isEmailInvalid = form.email?.length > 0 && !validateEmail(form.email);
    const isMissingRequired = !form.au_state || !form.first_given_name || !form.surname || !form.wwcc_number;

    // Update internal form when the 'record' prop changes (e.g., when clicking Edit)
    useEffect(() => {
        if (record) {
            setForm({
                ...record,
                birthdate: formatForInput(record.birthdate),
                expiry_date: formatForInput(record.expiry_date),
                status: record.status || 'PENDING',
                notes: record.notes || ''
            });
        } else {
            setForm(INITIAL_FORM);
        }
    }, [record, open]);

    const isPastExpiry = form.expiry_date && new Date(form.expiry_date) < new Date().setHours(0, 0, 0, 0);

    const handleInternalSave = () => {
        // Ensure dates are not empty strings if the database expects null
        const cleanedForm = {
            ...form,
            birthdate: form.birthdate || null,
            expiry_date: form.expiry_date || null,
            other_given_names: form.other_given_names || null,
            notes: form.notes || null
        };
        onSave(cleanedForm);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" keepMounted>
            <DialogTitle>{isEditing ? 'Edit WWCC Record' : 'New WWCC Record'}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        select label="State"
                        value={form.au_state || ''}
                        onChange={(e) => setForm({ ...form, au_state: e.target.value })}
                        sx={{ width: '150px' }}
                    >
                        {AU_STATES.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </TextField>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Surname" fullWidth value={form.surname || ''}
                            onChange={(e) => setForm({ ...form, surname: e.target.value })} />

                        <TextField label="First Name" fullWidth value={form.first_given_name || ''}
                            onChange={(e) => setForm({ ...form, first_given_name: e.target.value })} />

                    </Box>

                    <TextField label="Other Given Names" fullWidth value={form.other_given_names || ''}
                        onChange={(e) => setForm({ ...form, other_given_names: e.target.value })} />


                    <TextField label="Birthdate" type="date" fullWidth InputLabelProps={{ shrink: true }}
                        value={form.birthdate || ''} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} />

                    <TextField label="Email Address" type="email" fullWidth
                        value={form.email || ''}
                        error={isEmailInvalid}
                        helperText={(isEmailInvalid) ? "Please enter a valid email address." : " "}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="WWCC Number" fullWidth value={form.wwcc_number || ''}
                            onChange={(e) => setForm({ ...form, wwcc_number: e.target.value.toUpperCase() })} />
                        <TextField label="Expiry Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                            value={form.expiry_date || ''} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
                    </Box>

                    <TextField
                        select
                        fullWidth
                        label="Status"
                        value={form.status || 'PENDING'}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                    >
                        {STATUS_OPTIONS.map((opt) => (
                            <MenuItem
                                key={opt.value}
                                value={opt.value}
                                // Disable "Valid" if the date provided is actually in the past
                                disabled={isPastExpiry && opt.value === 'VALID'}
                            >
                                {opt.label} {isPastExpiry && opt.value === 'VALID' ? "(Date is in the past)" : ""}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Notes"
                        name="notes"
                        value={form.notes || ''}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        fullWidth
                        multiline
                        rows={3}
                        margin="normal"
                        variant="outlined"
                        placeholder="Add any internal compliance notes or historical remarks here..."
                        sx={{ mt: 2 }}
                    />

                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleInternalSave}
                    disabled={isMissingRequired || isEmailInvalid}
                >
                    {isEditing ? 'Update' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}